import { CsatPopoverFormFields } from '@/components/ui/csat-feedback/csat-popover-form-fields';
import { useDictationCsatForm } from '@/features/dictation/dictationBubble/csat/hooks/useDictationCsatForm';
import { Button } from '@heidi/design-system/button';
import { Form } from '@heidi/design-system/form';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { Progress } from '@heidi/design-system/progress';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import {
  dictationCsatPopoverStateAtom,
  getDictationAnalyticsPorts,
} from '@heidi/dictate';
import {
  DICTATION_CSAT_SURVEY_ACTION,
  DICTATION_FEEDBACK_HISTORY_HREF as DICTATION_LOGS_ROUTE,
} from '@heidi/dictate/contracts';
import { isTauri } from '@heidi/runtime/platform';
import { useAtomValue, useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

const AUTO_DISMISS_MS = 15_000;

/**
 * DictationCsatPopover
 *
 * Fixed bottom-right feedback popover shown after a successful dictation (probabilistically).
 * Uses a zero-size PopoverAnchor at the bottom-right corner so no trigger is needed.
 * Collects a 1–5 star rating and optional comment, then fires analytics.
 *
 * Phase 1 (no star selected): title + stars + X
 * Phase 2 (star selected): adds textarea + Dismiss/Submit buttons
 */
export const DictationCsatPopover = () => {
  const intl = useIntl();
  const router = useRouter();
  const { form, handleSubmit, handleDismiss } = useDictationCsatForm();
  const { reset } = form;
  const { isOpen, viewed } = useAtomValue(dictationCsatPopoverStateAtom);
  const setPopoverState = useSetAtom(dictationCsatPopoverStateAtom);

  // Desktop: confine the globally-mounted popover to the logs page. Web stays global.
  const isVisible = isOpen && (!isTauri() || router.pathname === DICTATION_LOGS_ROUTE);

  // Track `viewed` once per open, not on every persisted-open remount.
  useEffect(() => {
    if (!isVisible || viewed) return;
    reset();
    setPopoverState((prev) => ({ ...prev, viewed: true }));
    getDictationAnalyticsPorts().csat.trackViewed({
      view_action: DICTATION_CSAT_SURVEY_ACTION.shown,
    });
  }, [isVisible, viewed, reset, setPopoverState]);

  const starsValue = useWatch({ control: form.control, name: 'stars' });
  const hasStarSelected = starsValue !== undefined;

  // Auto-dismiss an unattended popover; cancelled once the user engages (picks a star).
  useEffect(() => {
    if (!isVisible || hasStarSelected) return;
    const timeoutId = setTimeout(handleDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timeoutId);
  }, [isVisible, hasStarSelected, handleDismiss]);

  const [countdownProgress, setCountdownProgress] = useState(100);

  useEffect(() => {
    if (!isVisible || hasStarSelected) {
      setCountdownProgress(100);
      return;
    }

    const startTime = performance.now();
    let rafId: number;

    const tick = () => {
      const remaining = Math.max(0, AUTO_DISMISS_MS - (performance.now() - startTime));
      setCountdownProgress((remaining / AUTO_DISMISS_MS) * 100);
      if (remaining > 0) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isVisible, hasStarSelected]);

  return (
    <Popover
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) {
          handleDismiss();
        }
      }}
    >
      <PopoverAnchor className="fixed bottom-4 right-4 size-0" />
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="relative w-full max-w-xs overflow-hidden"
      >
        <Form {...form}>
          <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-3">
            <Button
              type="button"
              variant="ghost"
              size="xxs"
              onClick={handleDismiss}
              className="absolute end-2 top-2"
              aria-label={intl.formatMessage({
                id: 'TDaF6JVgG6',
                defaultMessage: 'Dismiss',
              })}
            >
              <X className="size-4" />
            </Button>

            <TypographyP2Bold className="pe-6">
              <FormattedMessage
                id="DEEkFkVDai"
                defaultMessage="How was your dictation experience?"
              />
            </TypographyP2Bold>

            <CsatPopoverFormFields
              control={form.control}
              hasStarSelected={hasStarSelected}
              onDismiss={handleDismiss}
            />
          </form>
        </Form>

        {!hasStarSelected && (
          <Progress
            value={countdownProgress}
            aria-hidden="true"
            data-testid="csat-countdown-bar"
            className="absolute inset-x-0 bottom-0 h-1 w-full rounded-none bg-background-secondary [&>div]:bg-text-secondary"
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

DictationCsatPopover.displayName = 'DictationCsatPopover';
